import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, DialogActions, DialogContent, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Dialog, DialogTitle } from '../Common/ModalDialog';
import { API_GET_ACADEMIC_SESSIONS, API_GET_UNASSIGNED_FEE_SESSIONS, API_UPDATE_FEE } from '../Config/ApiUrlConstant';

const request = (url, body) => fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(async (response) => ({ response, payload: await response.json() }));
const asMonth = (value) => String(value || '').slice(0, 7);

export default function LegacyFeeResolution() {
  const [fees, setFees] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selection, setSelection] = useState({});
  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ payload: feePayload }, { payload: sessionPayload }] = await Promise.all([
        request(API_GET_UNASSIGNED_FEE_SESSIONS, {}),
        request(API_GET_ACADEMIC_SESSIONS, {}),
      ]);
      if (!feePayload.status) throw new Error(feePayload.message || 'Unable to load legacy fees.');
      setFees(feePayload.data || []);
      setSessions((sessionPayload.data || []).filter((session) => session.status !== 'ARCHIVED' && session.is_deleted !== 1));
    } catch (error) {
      setFeedback({ severity: 'error', message: error.message || 'Unable to load the legacy fee queue.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!target || !selection[target.id]) return;
    setSaving(true);
    try {
      const { response, payload } = await request(API_UPDATE_FEE, {
        id: target.id,
        student_id: target.student_id,
        academic_session_id: selection[target.id],
        fee_months: [asMonth(target.fee_month)],
        tuition_charge: target.tuition_charge ?? 0,
        magic_charge: target.magic_charge ?? 0,
        fee_pending: target.fee_pending ?? 0,
        description: target.description || '',
        discount: target.discount ?? 0,
      });
      if (!response.ok || !payload.status) throw new Error(payload.message || 'Unable to assign the academic session.');
      setTarget(null);
      setFeedback({ severity: 'success', message: `Academic session assigned for ${target.student_name}.` });
      await load();
    } catch (error) {
      setFeedback({ severity: 'error', message: error.message || 'Unable to assign the academic session.' });
    } finally {
      setSaving(false);
    }
  };

  const selectedSession = sessions.find((session) => String(session.id) === String(selection[target?.id]));
  return <Box>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
      <Box><Typography variant="h5">Legacy Fee Session Review</Typography><Typography color="text.secondary">Assign a session only after verifying the fee month and historical enrollment. No fee amount or history is deleted.</Typography></Box>
      <Button variant="outlined" onClick={load} disabled={loading}>Refresh queue</Button>
    </Stack>
    {feedback ? <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ mb: 2 }}>{feedback.message}</Alert> : null}
    <Paper sx={{ overflowX: 'auto' }}>
      <Table size="small"><TableHead><TableRow><TableCell>Student</TableCell><TableCell>Class</TableCell><TableCell>Fee month</TableCell><TableCell>Amount</TableCell><TableCell sx={{ minWidth: 220 }}>Academic session</TableCell><TableCell>Action</TableCell></TableRow></TableHead><TableBody>
        {loading ? <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell></TableRow> : null}
        {!loading && fees.map((fee) => <TableRow key={fee.id}><TableCell><strong>{fee.student_name}</strong><br /><Typography variant="caption">{fee.student_no || `Student #${fee.student_id}`}</Typography></TableCell><TableCell>{fee.class || '-'}</TableCell><TableCell>{asMonth(fee.fee_month)}</TableCell>{/* The existing record is only reassigned; amounts are submitted unchanged. */}<TableCell>{Number(fee.tuition_charge || 0) + Number(fee.magic_charge || 0) - Number(fee.discount || 0)}</TableCell><TableCell><TextField select fullWidth size="small" label="Select session" value={selection[fee.id] || ''} onChange={(event) => setSelection({ ...selection, [fee.id]: event.target.value })}><MenuItem value="">Select verified session</MenuItem>{sessions.map((session) => <MenuItem key={session.id} value={session.id}>{session.session_code} ({String(session.start_date).slice(0, 10)} to {String(session.end_date).slice(0, 10)})</MenuItem>)}</TextField></TableCell><TableCell><Button size="small" variant="contained" disabled={!selection[fee.id]} onClick={() => setTarget(fee)}>Assign</Button></TableCell></TableRow>)}
        {!loading && !fees.length ? <TableRow><TableCell colSpan={6} align="center">There are no active legacy fees awaiting an academic-session assignment.</TableCell></TableRow> : null}
      </TableBody></Table>
    </Paper>
    <Dialog open={Boolean(target)} onClose={() => !saving && setTarget(null)} fullWidth maxWidth="sm"><DialogTitle>Confirm academic-session assignment</DialogTitle><DialogContent><Typography>Assign <strong>{target?.student_name}</strong>'s fee for <strong>{asMonth(target?.fee_month)}</strong> to <strong>{selectedSession?.session_code || 'the selected session'}</strong>?</Typography><Typography color="text.secondary" sx={{ mt: 2 }}>This preserves the fee amount and creates an audit record. The session must include the fee month.</Typography></DialogContent><DialogActions><Button onClick={() => setTarget(null)} disabled={saving}>Cancel</Button><Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Assigning...' : 'Confirm assignment'}</Button></DialogActions></Dialog>
  </Box>;
}
